import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../../../../ui/components/Button";
import { TextField } from "../../../../ui/components/TextField";
import { TextArea } from "../../../../ui/components/TextArea";
import { Select } from "../../../../ui/components/Select";
import { DialogLayout } from "../../../../ui/layouts/DialogLayout";
import { Badge } from "../../../../ui/components/Badge";
import { Checkbox } from "../../../../ui/components/Checkbox";
import {
  FeatherPlus,
  FeatherEdit,
  FeatherTrash2,
  FeatherAlertTriangle,
  FeatherX,
  FeatherCalendar,
} from "@subframe/core";
import { useChecklistTemplate } from "../hooks/useChecklistTemplate";
import { ChecklistTemplateItemEntity } from "../../domain/entities/ChecklistTemplateEntity";
import { useToast } from "../../../../shared/presentation/components";
import {
  MONTH_END_CHECKLIST_ACCOUNTING_AREA_OPTIONS,
  CHECKLIST_ITEM_PRIORITY_OPTIONS,
  type ChecklistItemPriority,
} from "../../../../shared/constants/monthEndChecklist";

const ACCOUNTING_AREAS = MONTH_END_CHECKLIST_ACCOUNTING_AREA_OPTIONS;

export const ChecklistTemplatePage: React.FC = () => {
  const navigate = useNavigate();
  const {
    template,
    items,
    itemsByArea,
    loading,
    error,
    fetchTemplate,
    fetchItems,
    addTemplateItem,
    updateTemplateItem,
    deleteTemplateItem,
  } = useChecklistTemplate();
  const { showSuccess, showError } = useToast();

  const [itemDialogOpen, setItemDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editingItem, setEditingItem] =
    useState<ChecklistTemplateItemEntity | null>(null);
  const [itemToDelete, setItemToDelete] =
    useState<ChecklistTemplateItemEntity | null>(null);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteFromCurrentMonth, setDeleteFromCurrentMonth] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    accounting_area: "",
    title: "",
    description: "",
    task_guidance: [""], // Array of steps
    accepted_proof_types: ["screenshot"],
    requires_proof: true,
    display_order: items.length + 1,
    priority: "medium" as ChecklistItemPriority,
  });

  useEffect(() => {
    const loadData = async () => {
      try {
        await fetchTemplate();
        await fetchItems();
      } catch (err) {
        // Errors are handled in the hooks
        console.error("Failed to load template data:", err);
      }
    };
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run once on mount

  const handleAddItem = () => {
    setEditingItem(null);
    setFormData({
      accounting_area: "",
      title: "",
      description: "",
      task_guidance: [""],
      accepted_proof_types: ["screenshot"],
      requires_proof: true,
      display_order: items.length + 1,
      priority: "medium",
    });
    setItemDialogOpen(true);
  };

  const handleEditItem = (item: ChecklistTemplateItemEntity) => {
    setEditingItem(item);
    setFormData({
      accounting_area: item.accounting_area,
      title: item.title,
      description: item.description || "",
      task_guidance: item.task_guidance.length > 0 ? item.task_guidance : [""],
      accepted_proof_types: item.accepted_proof_types,
      requires_proof: item.requires_proof !== false,
      display_order: item.display_order,
      priority: item.priority ?? "medium",
    });
    setItemDialogOpen(true);
  };

  const handleDeleteClick = (item: ChecklistTemplateItemEntity) => {
    setItemToDelete(item);
    setDeleteDialogOpen(true);
  };

  const handleSaveItem = async () => {
    if (
      !formData.accounting_area ||
      !formData.title ||
      !formData.description?.trim()
    ) {
      showError("Accounting area, title, and description are required");
      return;
    }

    if (!template) {
      showError("Template not loaded");
      return;
    }

    try {
      setSaving(true);

      const taskGuidance = formData.task_guidance.filter(
        (step) => step.trim() !== "",
      );

      if (editingItem) {
        const { current_month_sync } = await updateTemplateItem(
          editingItem.id,
          {
            accounting_area: formData.accounting_area,
            title: formData.title,
            description: formData.description,
            task_guidance: taskGuidance,
            accepted_proof_types: formData.accepted_proof_types,
            requires_proof: formData.requires_proof,
            order_index: formData.display_order,
            priority: formData.priority,
          },
        );

        let msg = "Template item updated successfully.";
        if (
          !current_month_sync.applied &&
          current_month_sync.skipped_reason ===
            "current_month_item_already_completed"
        ) {
          msg +=
            " The linked current-month task is already completed, so its line was not overwritten.";
        }
        showSuccess(msg);
      } else {
        await addTemplateItem({
          template_id: template.id,
          accounting_area: formData.accounting_area,
          title: formData.title,
          description: formData.description,
          task_guidance: taskGuidance,
          accepted_proof_types: formData.accepted_proof_types,
          requires_proof: formData.requires_proof,
          order_index: formData.display_order,
          priority: formData.priority,
        });
        showSuccess("Template item added successfully");
      }

      setItemDialogOpen(false);
      setEditingItem(null);
    } catch (error: any) {
      showError(error.message || "Failed to save template item");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!itemToDelete) return;

    try {
      setDeleting(true);

      // Call delete API - it will handle soft delete on the backend
      await deleteTemplateItem(itemToDelete.id, deleteFromCurrentMonth);

      showSuccess("Template item deleted successfully");
      setDeleteDialogOpen(false);
      setItemToDelete(null);
      setDeleteFromCurrentMonth(false); // Reset checkbox
      // Items are automatically updated locally by the hook, no need to refetch
    } catch (error: any) {
      showError(error.message || "Failed to delete template item");
    } finally {
      setDeleting(false);
    }
  };

  const handleAddTaskStep = () => {
    setFormData({
      ...formData,
      task_guidance: [...formData.task_guidance, ""],
    });
  };

  const handleRemoveTaskStep = (index: number) => {
    setFormData({
      ...formData,
      task_guidance: formData.task_guidance.filter((_, i) => i !== index),
    });
  };

  const handleTaskStepChange = (index: number, value: string) => {
    const newSteps = [...formData.task_guidance];
    newSteps[index] = value;
    setFormData({
      ...formData,
      task_guidance: newSteps,
    });
  };

  return (
    <div className="flex w-full flex-col items-start gap-6 p-8">
      {/* Header */}
      <div className="flex w-full items-center justify-between">
        <div className="flex flex-col items-start gap-1">
          <span className="text-heading-2 font-heading-2 text-default-font">
            Month End Checklist Template
          </span>
          <span className="text-body font-body text-subtext-color">
            Manage the template for monthly checklist items
          </span>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="neutral-secondary"
            icon={<FeatherCalendar />}
            onClick={() => navigate("/admin/month-end-checklists")}
          >
            View Monthly Checklists
          </Button>
          {template && (
            <Button
              variant="brand-primary"
              icon={<FeatherPlus />}
              onClick={handleAddItem}
            >
              Add Item
            </Button>
          )}
        </div>
      </div>

      {/* Loading State */}
      {loading && !template && !error && (
        <div className="flex w-full items-center justify-center p-8">
          <span className="text-body font-body text-subtext-color">
            Loading template...
          </span>
        </div>
      )}

      {/* No Template State */}
      {!loading && !template && !error && (
        <div className="flex w-full flex-col items-center justify-center gap-4 p-12 bg-neutral-50 border border-neutral-border rounded-lg">
          <FeatherAlertTriangle className="w-12 h-12 text-subtext-color" />
          <div className="flex flex-col items-center gap-2">
            <span className="text-heading-3 font-heading-3 text-default-font">
              No Template Found
            </span>
            <span className="text-body font-body text-subtext-color text-center max-w-md">
              There is no active month-end checklist template. Please create one
              to get started.
            </span>
            <Button
              variant="brand-primary"
              onClick={() => {
                navigate("/admin/month-end-checklist/create-template");
              }}
            >
              Create Template
            </Button>
          </div>
        </div>
      )}

      {/* Error Display (only for actual errors, not "no template") */}
      {error && (
        <div className="flex w-full flex-col items-start gap-2 p-4 bg-red-50 border border-red-200 rounded-lg">
          <span className="text-sm font-medium text-red-800">
            {error.message}
          </span>
          <Button
            variant="neutral-secondary"
            onClick={async () => {
              try {
                await fetchTemplate();
                await fetchItems();
              } catch (err) {
                console.error("Failed to retry:", err);
              }
            }}
          >
            Retry
          </Button>
        </div>
      )}

      {/* Template Info */}
      {/* {template && (
        <div className="flex w-full flex-col items-start gap-2 p-4 bg-neutral-50 border border-neutral-border rounded-lg">
          <span className="text-body font-body font-semibold text-default-font">
            Template: {template.name}
          </span>
          {template.description && (
            <span className="text-body font-body text-subtext-color">
              {template.description}
            </span>
          )}
        </div>
      )} */}

      {/* Items by Accounting Area Cards - Only show when template exists */}
      {template && (
        <div className="w-full">
          {loading ? (
            <div className="flex items-center justify-center p-8">
              <span className="text-body font-body text-subtext-color">
                Loading template items...
              </span>
            </div>
          ) : Object.keys(itemsByArea).length === 0 ? (
            <div className="flex flex-col items-center justify-center p-8 gap-4 border border-solid border-neutral-border rounded-lg">
              <span className="text-body font-body text-subtext-color">
                No template items found. Add your first item to get started.
              </span>
              <Button variant="brand-primary" onClick={handleAddItem}>
                Add Item
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {Object.entries(itemsByArea)
                .sort(([areaA], [areaB]) => areaA.localeCompare(areaB))
                .map(([accountingArea, areaItems]) => (
                  <div
                    key={accountingArea}
                    className="flex flex-col gap-4 p-6 border border-solid border-neutral-border rounded-lg bg-default-background"
                  >
                    {/* Card Header */}
                    <div className="flex items-center justify-between border-b border-solid border-neutral-border pb-3">
                      <span className="text-heading-3 font-heading-3 text-default-font">
                        {accountingArea}
                      </span>
                      <Badge variant="neutral" className="text-xs">
                        {areaItems.length} task
                        {areaItems.length !== 1 ? "s" : ""}
                      </Badge>
                    </div>

                    {/* Items List */}
                    <div className="flex flex-col gap-3">
                      {areaItems
                        .sort((a, b) => a.display_order - b.display_order)
                        .map((item) => (
                          <div
                            key={item.id}
                            className={`flex flex-col gap-2 p-3 border border-solid border-neutral-border rounded ${
                              !item.is_active
                                ? "opacity-50 bg-neutral-50"
                                : "bg-default-background"
                            }`}
                          >
                            <div className="flex items-start justify-between gap-2">
                              <div className="flex flex-col gap-1 flex-1">
                                <div className="flex items-center gap-2">
                                  <span className="text-body font-body font-semibold text-default-font">
                                    {item.title}
                                  </span>
                                  {!item.is_active && (
                                    <Badge
                                      variant="neutral"
                                      className="text-xs"
                                    >
                                      Inactive
                                    </Badge>
                                  )}
                                </div>
                                {item.description && (
                                  <span className="text-sm text-body font-body text-subtext-color line-clamp-2">
                                    {item.description}
                                  </span>
                                )}
                                <span className="text-xs text-body font-body text-subtext-color">
                                  {item.task_guidance.length} step
                                  {item.task_guidance.length !== 1 ? "s" : ""} •
                                  Order: {item.display_order} • Priority:{" "}
                                  {(item.priority ?? "medium").toUpperCase()}
                                </span>
                              </div>
                              <div className="flex items-center gap-1">
                                <Button
                                  variant="neutral-tertiary"
                                  icon={<FeatherEdit />}
                                  onClick={() => handleEditItem(item)}
                                />
                                <Button
                                  variant="neutral-tertiary"
                                  icon={<FeatherTrash2 />}
                                  onClick={() => handleDeleteClick(item)}
                                />
                              </div>
                            </div>
                          </div>
                        ))}
                    </div>
                  </div>
                ))}
            </div>
          )}
        </div>
      )}

      {/* Add/Edit Item Dialog */}
      <DialogLayout
        open={itemDialogOpen}
        onOpenChange={(open) => {
          if (!open && !saving) {
            setItemDialogOpen(false);
            setEditingItem(null);
          }
        }}
        className="max-w-[1200px] w-[90vw]"
      >
        <div className="flex w-full flex-col items-start gap-6 bg-default-background px-8 py-6">
          <div className="flex w-full items-center justify-between">
            <span className="text-heading-2 font-heading-2 text-default-font">
              {editingItem ? "Edit Template Item" : "Add Template Item"}
            </span>
            <Button
              variant="neutral-tertiary"
              icon={<FeatherX />}
              onClick={() => {
                if (!saving) {
                  setItemDialogOpen(false);
                  setEditingItem(null);
                }
              }}
            />
          </div>
          <div className="flex flex-col gap-4 w-full">
            <Select
              label="Accounting Area"
              value={formData.accounting_area}
              onValueChange={(value: string) => {
                setFormData({ ...formData, accounting_area: value || "" });
              }}
              placeholder="Select Accounting Area"
            >
              {ACCOUNTING_AREAS.map((area) => (
                <Select.Item key={area.value} value={area.value}>
                  {area.label}
                </Select.Item>
              ))}
            </Select>

            <Select
              label="Priority"
              value={formData.priority}
              onValueChange={(value: string) => {
                const v = value as ChecklistItemPriority;
                if (v === "high" || v === "medium" || v === "low") {
                  setFormData({ ...formData, priority: v });
                }
              }}
              placeholder="Select priority"
            >
              {CHECKLIST_ITEM_PRIORITY_OPTIONS.map((p) => (
                <Select.Item key={p.value} value={p.value}>
                  {p.label}
                </Select.Item>
              ))}
            </Select>

            <TextField label="Title">
              <TextField.Input
                value={formData.title}
                onChange={(e) =>
                  setFormData({ ...formData, title: e.target.value })
                }
                placeholder="Enter item title"
              />
            </TextField>

            <TextArea label="Description *">
              <TextArea.Input
                value={formData.description}
                onChange={(e) =>
                  setFormData({ ...formData, description: e.target.value })
                }
                placeholder="Enter item description"
              />
            </TextArea>

            <div className="flex flex-col gap-2">
              <span className="text-caption-bold font-caption-bold text-default-font">
                Task Guidance (Steps)
              </span>
              {formData.task_guidance.map((step, index) => (
                <div key={index} className="flex items-center gap-2">
                  <TextField className="flex-1">
                    <TextField.Input
                      value={step}
                      onChange={(e) =>
                        handleTaskStepChange(index, e.target.value)
                      }
                      placeholder={`Step ${index + 1}`}
                    />
                  </TextField>
                  {formData.task_guidance.length > 1 && (
                    <Button
                      variant="neutral-tertiary"
                      icon={<FeatherX />}
                      onClick={() => handleRemoveTaskStep(index)}
                    />
                  )}
                </div>
              ))}
              <Button
                variant="neutral-secondary"
                onClick={handleAddTaskStep}
                icon={<FeatherPlus />}
              >
                Add Step
              </Button>
            </div>

            <TextField label="Display Order">
              <TextField.Input
                type="number"
                value={formData.display_order.toString()}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    display_order: parseInt(e.target.value) || 0,
                  })
                }
              />
            </TextField>

            <Checkbox
              checked={formData.requires_proof}
              onCheckedChange={(checked) =>
                setFormData({
                  ...formData,
                  requires_proof: Boolean(checked),
                })
              }
              label="Requires proof before marking done (screenshot / attachment)"
            />

            <div className="flex items-center justify-end gap-2 pt-4">
              <Button
                variant="neutral-secondary"
                onClick={() => {
                  if (!saving) {
                    setItemDialogOpen(false);
                    setEditingItem(null);
                  }
                }}
                disabled={saving}
              >
                Cancel
              </Button>
              <Button
                variant="brand-primary"
                onClick={handleSaveItem}
                disabled={saving}
              >
                {saving ? "Saving..." : editingItem ? "Update" : "Add"}
              </Button>
            </div>
          </div>
        </div>
      </DialogLayout>

      {/* Delete Confirmation Dialog */}
      <DialogLayout
        open={deleteDialogOpen}
        onOpenChange={(open) => {
          if (!open && !deleting) {
            setDeleteDialogOpen(false);
            setItemToDelete(null);
          }
        }}
      >
        <div className="flex w-full max-w-[1000px] flex-col items-start gap-6 bg-default-background px-8 py-6">
          <div className="flex w-full items-center justify-between">
            <span className="text-heading-2 font-heading-2 text-default-font">
              Delete Template Item
            </span>
            <Button
              variant="neutral-tertiary"
              icon={<FeatherX />}
              onClick={() => {
                if (!deleting) {
                  setDeleteDialogOpen(false);
                  setItemToDelete(null);
                  setDeleteFromCurrentMonth(false); // Reset checkbox
                }
              }}
            />
          </div>
          <div className="flex flex-col gap-4 w-full">
            <div className="flex items-start gap-3">
              <FeatherAlertTriangle className="h-5 w-5 text-error-600 mt-0.5" />
              <div className="flex flex-col gap-1">
                <span className="text-body font-body text-default-font">
                  Are you sure you want to delete this template item?
                </span>
                {itemToDelete && (
                  <span className="text-body font-body text-subtext-color">
                    This will delete "{itemToDelete.title}" from the template.
                  </span>
                )}
              </div>
            </div>
            <div className="flex flex-col gap-2 pt-2">
              <Checkbox
                checked={deleteFromCurrentMonth}
                onCheckedChange={(checked) =>
                  setDeleteFromCurrentMonth(checked ?? false)
                }
                disabled={deleting}
                label="Also delete from current month's checklist"
              />
              <div className="text-sm text-body font-body text-subtext-color pl-6">
                By default, items in the current month's checklist will be
                preserved. Check this box to also delete them from the current
                month.
              </div>
            </div>
            <div className="flex items-center justify-end gap-2 pt-4">
              <Button
                variant="neutral-secondary"
                onClick={() => {
                  if (!deleting) {
                    setDeleteDialogOpen(false);
                    setItemToDelete(null);
                    setDeleteFromCurrentMonth(false); // Reset checkbox
                  }
                }}
                disabled={deleting}
              >
                Cancel
              </Button>
              <Button
                variant="destructive-primary"
                onClick={handleDeleteConfirm}
                disabled={deleting}
              >
                {deleting ? "Deleting..." : "Delete"}
              </Button>
            </div>
          </div>
        </div>
      </DialogLayout>
    </div>
  );
};
